import { Tooltip as BaseUiTooltip } from "@base-ui/react/tooltip"
import React, { createContext, useContext, useMemo } from "react"

import classNames from "classnames"

import { TOOLTIP_DELAY } from "shared/constants/tooltip"
import { stopPropagation } from "shared/lib/stopPropagation"
import { PopoverArrow } from "shared/ui/popover/ui/PopoverArrow"

import "./styles/_tooltip.scss"

interface TooltipProps {
  content?: React.ReactNode
  children: React.ReactElement
  placement?: "top" | "bottom" | "left" | "right"
  className?: string
  delay?: number
  disabled?: boolean
  alignOffset?: number
  sideOffset?: number
  open?: boolean
}

const TooltipComponent = React.memo(
  ({
    content,
    children,
    placement = "right",
    delay = TOOLTIP_DELAY,
    className,
    disabled,
    alignOffset,
    sideOffset,
    open
  }: TooltipProps) => {
    const { disabled: disabledByProvider } = useTooltipProvider()

    return (
      <BaseUiTooltip.Root
        open={open}
        disabled={disabled || disabledByProvider || (!content && open !== true)}
      >
        <BaseUiTooltip.Trigger render={children} delay={delay} />
        <BaseUiTooltip.Portal>
          <BaseUiTooltip.Positioner
            className="tooltip-positioner"
            side={placement}
            alignOffset={alignOffset}
            sideOffset={sideOffset}
            positionMethod="fixed"
          >
            <BaseUiTooltip.Popup
              className={classNames("tooltip", className)}
              aria-live="polite"
              onClick={stopPropagation}
            >
              <BaseUiTooltip.Arrow className="tooltip__arrow">
                <PopoverArrow
                  fillClassName="tooltip__arrow-fill"
                  outerStrokeClassName="tooltip__arrow-outer-stroke"
                  innerStrokeClassName="tooltip__arrow-inner-stroke"
                />
              </BaseUiTooltip.Arrow>
              <div className="tooltip__content">{content}</div>
            </BaseUiTooltip.Popup>
          </BaseUiTooltip.Positioner>
        </BaseUiTooltip.Portal>
      </BaseUiTooltip.Root>
    )
  }
)

interface TooltipProviderContextType {
  disabled: boolean
}

const TooltipProviderContext = createContext<TooltipProviderContextType>({
  disabled: false
})

export const useTooltipProvider = () => useContext(TooltipProviderContext)

interface CustomTooltipProviderProps {
  children: React.ReactNode
  disabled?: boolean
  delay?: number
  timeout?: number
  closeDelay?: number
}

export function CustomTooltipProvider({
  children,
  disabled = false,
  delay = TOOLTIP_DELAY,
  timeout,
  closeDelay
}: CustomTooltipProviderProps) {
  const contextValue = useMemo(() => ({ disabled }), [disabled])

  return (
    <TooltipProviderContext.Provider value={contextValue}>
      <BaseUiTooltip.Provider
        delay={delay}
        timeout={timeout}
        closeDelay={closeDelay}
      >
        {children}
      </BaseUiTooltip.Provider>
    </TooltipProviderContext.Provider>
  )
}

type TooltipType = typeof TooltipComponent & {
  Provider: typeof CustomTooltipProvider
}

const Tooltip = TooltipComponent as TooltipType
Tooltip.Provider = CustomTooltipProvider

export { Tooltip }
